// Everything this app keeps in the browser for the signed-in person, and how
// it is cleared when they sign out (aithlo-frontend#46).
//
// A prefix sweep, not a list: the list is what drifted (logout used to clear
// only 'user', and the coaching roster, the v2 me cache and the v2 offline
// copy all survived into the next account on the same browser).

const APP_PREFIXES = ['aithlo', 'calendar_', 'v2:'];
const APP_KEYS = new Set(['user', 'weekStartDay', 'calendarViewMode', 'viewingAthlete']);

// Written by code that no longer exists; nothing reads them (#46).
export const ORPHAN_KEYS = [
  'calendar_manual_sessions',
  'calendar_deleted_sessions',
  'calendar_disconnected_pairs',
  'aithlo_conversation_id',
];

/** The v2 client's IndexedDB store (src/v2/persist.js). */
export const V2_DB = 'aithlo-v2';
export const V2_STORE = 'kv';

export const isAppKey = (key) =>
  typeof key === 'string' && (APP_KEYS.has(key) || APP_PREFIXES.some((p) => key.startsWith(p)));

const safe = (get) => { try { return get() || null; } catch { return null; } };

function sweep(storage) {
  if (!storage) return;
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) keys.push(storage.key(i));
    keys.filter(isAppKey).forEach((k) => { try { storage.removeItem(k); } catch { /* ignore */ } });
  } catch { /* blocked storage: nothing to clear */ }
}

// Empties the store rather than deleting the database: a delete waits on every
// open connection, and the outgoing client's may still be open — a new
// sign-in on the same page would then hang behind it.
function clearV2Store(idb) {
  if (!idb) return Promise.resolve();
  return new Promise((resolve) => {
    let req;
    try { req = idb.open(V2_DB, 1); } catch { resolve(); return; }
    req.onupgradeneeded = () => req.result.createObjectStore(V2_STORE);
    req.onerror = () => resolve();
    req.onsuccess = () => {
      const db = req.result;
      try {
        const t = db.transaction(V2_STORE, 'readwrite');
        t.objectStore(V2_STORE).clear();
        const done = () => { try { db.close(); } catch { /* ignore */ } resolve(); };
        t.oncomplete = done; t.onerror = done; t.onabort = done;
      } catch { try { db.close(); } catch { /* ignore */ } resolve(); }
    };
  });
}

/** Clear every app-owned key in local and session storage, and the v2 offline copy. */
export function clearUserStorage({
  local = safe(() => globalThis.localStorage),
  session = safe(() => globalThis.sessionStorage),
  idb = safe(() => globalThis.indexedDB),
} = {}) {
  sweep(local);
  sweep(session);
  return clearV2Store(idb);
}

/** Drop the keys nothing reads any more. */
export function dropOrphanKeys(local = safe(() => globalThis.localStorage)) {
  if (!local) return;
  ORPHAN_KEYS.forEach((k) => { try { local.removeItem(k); } catch { /* ignore */ } });
}

/** The signed-in user's id from the stored user, read synchronously (or null). */
export function storedUserId(local = safe(() => globalThis.localStorage)) {
  try {
    const u = JSON.parse(local?.getItem('user') || 'null');
    return u?.id ?? u?.user_id ?? null;
  } catch {
    return null;
  }
}
